import React, { useRef, useState } from 'react';
import {
    Modal,
    Box,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import CommonButton from '../../../../common/components/Buttons/CommonButton';
import { ForceCloseIcon } from '../../../../assets/CommonAssets';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 650 },
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    p: 3,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '85vh',
};

const sectionFieldsetStyle = {
    component: 'fieldset',
    position: 'relative',
    bgcolor: '#f5f5f5',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    pt: 2.5,
    pb: 1.5,
    px: 2,
    margin: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 1.5,
};

const legendTitleStyle = {
    component: 'legend',
    px: 1,
    fontSize: '0.875rem',
    color: 'text.secondary',
    fontWeight: 'bold'
};

const actionElementStyle = {
    position: 'absolute',
    top: 6,
    right: 6,
};

export default function SystematicExamModal(props) {
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef(null);
    const [isEditingSec1, setIsEditingSec1] = useState(false);
    const [isEditingSec2, setIsEditingSec2] = useState(false);

    const [primaryProvidersData, setPrimaryProvidersData] = useState([
        { label: 'RS', value: 'Value 1' },
        { label: 'CVS', value: 'Value 2' },
        { label: 'CNS', value: 'Value 3' },
        { label: 'PA', value: 'Value 4' },
        { label: 'Other', value: 'Value 5' },
    ]);

    const [legendData, setLegendData] = useState([
        { label: 'RS', value: 'Legend Value 1' },
        { label: 'CVS', value: 'Legend Value 2' },
        { label: 'CNS', value: 'Legend Value 3' },
        { label: 'PA', value: 'Legend Value 4' },
        { label: 'Other', value: 'Legend Value 5' },
    ]);

    // Handles passing the template payload back to parent component 
    const handleClose = (templateData = null) => {
        if (props?.onClose) {
            props.onClose(templateData);
        } else if (props?.setOpenSystemeticExam) {
            props.setOpenSystemeticExam(false);
        }
        setSearchTerm('');
        setIsEditingSec1(false);
        setIsEditingSec2(false);
    };

    const handlePrimaryChange = (label, newValue) => {
        setPrimaryProvidersData(prev => prev.map(item => item.label === label ? { ...item, value: newValue } : item));
    };

    const handleLegendChange = (label, newValue) => {
        setLegendData(prev => prev.map(item => item.label === label ? { ...item, value: newValue } : item));
    };

    const filteredPrimary = primaryProvidersData.filter(
        (item) =>
            item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.value.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredLegend = legendData.filter(
        (item) =>
            item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.value.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const hasResults = filteredPrimary.length > 0 || filteredLegend.length > 0;
    
    React?.useEffect(() => {
        if (props?.open) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 50);
        }
    }, [props?.open]);

    return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
            <Modal open={props?.open} onClose={() => handleClose(null)} aria-labelledby="search-modal-title">
                <Box sx={modalStyle}>

                    {/* Header Row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography id="search-modal-title" variant="h6" component="h2" fontWeight="bold">
                            Doctor Template
                        </Typography>
                        <IconButton onClick={() => handleClose(null)} size="small">
                            <ForceCloseIcon />
                        </IconButton>
                    </Box>

                    {/* Search Bar */}
                    <TextField
                        inputRef={searchInputRef}
                        fullWidth
                        size="small"
                        placeholder="Search fields..."
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Content Container */}
                    <Box sx={{ overflowY: 'auto', flexGrow: 1, pr: 1 }}>
                        {hasResults ? (
                            <>
                                {/* ================= SECTION 1: TEMPLATE 1 ================= */}
                                {filteredPrimary.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Box
                                            {...sectionFieldsetStyle}
                                            onClick={() => {
                                                if (!isEditingSec1) {
                                                    const templatePayload = {};
                                                    filteredPrimary.forEach((item) => {
                                                        const label = item.label.trim().toLowerCase();
                                                        let targetField = "";
                                                        if (label === "rs") targetField = "rs";
                                                        else if (label === "cvs") targetField = "cvs";
                                                        else if (label === "cns") targetField = "cns";
                                                        else if (label === "pa") targetField = "pa";
                                                        else if (label === "other") targetField = "seOther";

                                                        if (targetField) {
                                                            templatePayload[targetField] = item.value;
                                                        }
                                                    });
                                                    handleClose(templatePayload);
                                                }
                                            }}
                                            sx={{
                                                ...sectionFieldsetStyle?.sx,
                                                cursor: isEditingSec1 ? 'default' : 'pointer',
                                                transition: 'all 0.15s ease-in-out',
                                                '&:hover': !isEditingSec1 ? {
                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                    borderColor: 'primary.main',
                                                    boxShadow: '0px 2px 8px rgba(0,0,0,0.08)'
                                                } : {}
                                            }}
                                        >
                                            <Typography {...legendTitleStyle}>Template 1</Typography>

                                            <Box sx={actionElementStyle} onClick={(e) => e.stopPropagation()}>
                                                {!isEditingSec1 && (
                                                    <IconButton
                                                        onClick={() => setIsEditingSec1(true)}
                                                        size="small"
                                                        color="primary"
                                                        sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', padding: '4px' }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>

                                            {filteredPrimary.map((item, index) => (
                                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => isEditingSec1 && e.stopPropagation()}>
                                                    <Typography variant="body2" fontWeight="medium" sx={{ whiteSpace: 'nowrap', py: 0.25 }}>
                                                        {item.label} :
                                                    </Typography>
                                                    {isEditingSec1 ? (
                                                        <TextField
                                                            size="small"
                                                            value={item.value}
                                                            onChange={(e) => handlePrimaryChange(item.label, e.target.value)}
                                                            sx={{ width: '100%', '& .MuiInputBase-input': { py: 0.25, px: 0.75, fontSize: '0.875rem' } }}
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" sx={{ py: 0.25, pl: 0.5 }}>{item.value}</Typography>
                                                    )}
                                                </Box>
                                            ))}

                                            {isEditingSec1 && (
                                                <div className='flex justify-end mt-2' onClick={(e) => e.stopPropagation()}>
                                                    <CommonButton
                                                        label={"Save Changes"}
                                                        onClick={() => setIsEditingSec1(false)}
                                                        className={"bg-customGreen text-white rounded px-3 py-1 text-xs font-semibold"}
                                                    />
                                                </div>
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                {/* ================= SECTION 2: TEMPLATE 2 ================= */}
                                {filteredLegend.length > 0 && (
                                    <Box sx={{ mb: 1 }}>
                                        <Box
                                            {...sectionFieldsetStyle}
                                            onClick={() => {
                                                if (!isEditingSec2) {
                                                    const templatePayload = {};
                                                    filteredLegend.forEach((item) => {
                                                        const label = item.label.trim().toLowerCase();
                                                        let targetField = "";
                                                        if (label === "rs") targetField = "rs";
                                                        else if (label === "cvs") targetField = "cvs";
                                                        else if (label === "cns") targetField = "cns";
                                                        else if (label === "pa") targetField = "pa";
                                                        else if (label === "other") targetField = "seOther";

                                                        if (targetField) {
                                                            templatePayload[targetField] = item.value;
                                                        }
                                                    });
                                                    handleClose(templatePayload);
                                                }
                                            }}
                                            sx={{
                                                ...sectionFieldsetStyle?.sx,
                                                cursor: isEditingSec2 ? 'default' : 'pointer',
                                                transition: 'all 0.15s ease-in-out',
                                                '&:hover': !isEditingSec2 ? {
                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                    borderColor: 'primary.main',
                                                    boxShadow: '0px 2px 8px rgba(0,0,0,0.08)'
                                                } : {}
                                            }}
                                        >
                                            <Typography {...legendTitleStyle}>Template 2</Typography>

                                            <Box sx={actionElementStyle} onClick={(e) => e.stopPropagation()}>
                                                {!isEditingSec2 && (
                                                    <IconButton
                                                        onClick={() => setIsEditingSec2(true)}
                                                        size="small"
                                                        color="primary"
                                                        sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', padding: '4px' }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>

                                            {filteredLegend.map((item, index) => (
                                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => isEditingSec2 && e.stopPropagation()}>
                                                    <Typography variant="body2" fontWeight="medium" sx={{ whiteSpace: 'nowrap', py: 0.25 }}>
                                                        {item.label} :
                                                    </Typography>
                                                    {isEditingSec2 ? (
                                                        <TextField
                                                            size="small"
                                                            value={item.value}
                                                            onChange={(e) => handleLegendChange(item.label, e.target.value)}
                                                            sx={{ width: '100%', '& .MuiInputBase-input': { py: 0.25, px: 0.75, fontSize: '0.875rem' } }}
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" sx={{ py: 0.25, pl: 0.5 }}>{item.value}</Typography>
                                                    )}
                                                </Box>
                                            ))}

                                            {isEditingSec2 && (
                                                <div className='flex justify-end mt-2' onClick={(e) => e.stopPropagation()}>
                                                    <CommonButton
                                                        label={"Save Changes"}
                                                        onClick={() => setIsEditingSec2(false)}
                                                        className={"bg-customGreen text-white rounded px-3 py-1 text-xs font-semibold"}
                                                    />
                                                </div>
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ my: 3 }}>
                                No matching results found.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}